<template>
  <BaseForm
    fullHeight
    @submit="onSubmit"
  >
    <div :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]">
      <div :class="$style.main">
        <div
          v-if="isEmpty"
          :class="$style.emptyState"
        >
          <EmptyState />
          <Favorites v-if="$screen === 's'" />
        </div>
        <div
          v-if="!isEmpty"
          :class="$style.details"
        >
          <div :class="$style.listTop">
            <OrderItems />
            <BaseButton
              v-if="$screen === 's'"
              fullWidth
              color="inverted"
              @click="showMobileBrowser = true"
            >
              {{ filters.capitalize(
                    $t('global.actions.add_item', {
                      item: $t('global.items.item', 2)
                    })
                  ) }}
            </BaseButton>
          </div>
          <div :class="$style.listBottom">
            <div :class="$style.total">
              <BaseHeading size="s">{{ $t('global.total') }}</BaseHeading>
              <div
                :class="[
                    $style.totalAmount,
                    {
                      [$style.animated]: animateTotal
                    }
                  ]"
              >
                <BaseHeading
                  size="xl"
                  v-test="'order-amount'"
                >
                  {{ filters.currency(totalPrice) }}
                </BaseHeading>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer v-if="!isEmpty" />
    </div>
  </BaseForm>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { inject, ref, watch } from 'vue';
import filters from '@/filters';
import Favorites from '@/modules/register/builder/browser/pages/default/favorites/index.vue';
import { useRegisterBrowserStore } from '@/modules/register/builder/browser/store';
import { useRegisterOrderStore } from '@/modules/register/stores/order';
import EmptyState from './EmptyState.vue';
import Footer from './Footer.vue';
import OrderItems from './items/index.vue';
import { useOrderSubmit } from './submit';

const { isEmpty, totalPrice } = storeToRefs(useRegisterOrderStore());
const { showMobileBrowser } = storeToRefs(useRegisterBrowserStore());
const { submit } = useOrderSubmit();

const triggerSidebarValidation = inject<
  (callback: (valid: boolean) => void) => void
>('triggerSidebarValidation', (cb) => cb(true));

const onSubmit = () => {
  triggerSidebarValidation((valid) => {
    if (valid) submit();
  });
};

const animateTotal = ref(false);
watch(totalPrice, () => {
  animateTotal.value = true;
  setTimeout(() => {
    animateTotal.value = false;
  }, 100);
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  justify-content: space-between;
  flex-direction: column;

  &:not(.smallScreen) {
    height: 100%;
  }
}

.main {
  display: flex;
  justify-content: space-between;
  flex-direction: column;

  .base:not(.smallScreen) & {
    flex: 1 1 auto;
    min-height: 0;
  }

  .base.smallScreen & {
    padding-bottom: $input-height + $spacing * 2;
  }
}

.emptyState {
  display: flex;
  flex-direction: column;
  gap: $spacing * 1.5;

  .base.smallScreen & {
    padding: 0 $spacing;
  }

  .base:not(.smallScreen) & {
    height: 100%;
  }
}

.details {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;

  .base:not(.smallScreen) & {
    height: 100%;
  }
}

.listTop {
  display: flex;
  flex-direction: column;
  gap: $spacing;
  padding: $spacing;

  .base:not(.smallScreen) & {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
}

.listBottom {
  display: flex;
  justify-content: flex-end;
  gap: $spacing;
  flex-shrink: 0;
  padding-left: $spacing;
  padding-right: $spacing;
  padding-bottom: $spacing;

  .base:not(.smallScreen) & {
    background-color: var(--color-bg-secondary);
    position: relative;
    padding-top: $spacing * .75;

    &:after {
      content: "";
      position: absolute;
      height: $spacing;
      left: 0;
      top: $spacing * -1;
      width: 100%;
      z-index: 1;
      background: linear-gradient(to top, var(--color-bg-secondary), transparent);
    }
  }
}

.total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.totalAmount {
  transition: transform 0.3s ease;
  transform-origin: right center;

  &.animated {
    transform: scale(1.15);
  }
}
</style>
